import { useMemo, useState } from "react";
import {
  Modal,
  Upload,
  Button,
  Table,
  Space,
  Typography,
  Alert,
  message,
  Input,
  InputNumber,
  Tooltip,
} from "antd";
import { DownloadOutlined, UploadOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { UploadFile } from "antd/es/upload/interface";
import {
  downloadBookingProductImportTemplate,
  parseBookingProductImportFile,
  productsToPreviewRows,
  toImportedBookingProduct,
  validateImportedBookingProduct,
  type BookingProductImportField,
  type BookingProductImportValidationKey,
  type ImportedBookingProduct,
  type ImportPreviewRow,
} from "../../utils/bookingProductBulkImport";

const { Text } = Typography;

interface BookingProductBulkImportModalProps {
  open: boolean;
  onCancel: () => void;
  onImport: (products: ImportedBookingProduct[]) => void;
}

export const BookingProductBulkImportModal = ({
  open,
  onCancel,
  onImport,
}: BookingProductBulkImportModalProps) => {
  const { t } = useTranslation();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [preview, setPreview] = useState<ImportPreviewRow[]>([]);
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [parsing, setParsing] = useState(false);

  const validationMessage = (key: BookingProductImportValidationKey) =>
    t(`bookingWizard.productBulkImport.validation.${key}`);

  const rowErrors = useMemo(
    () => preview.map((row) => validateImportedBookingProduct(row)),
    [preview]
  );

  const invalidRowCount = rowErrors.filter(
    (errors) => Object.keys(errors).length > 0
  ).length;
  const canImport = preview.length > 0 && invalidRowCount === 0;

  const reset = () => {
    setFileList([]);
    setPreview([]);
    setParseErrors([]);
    setParsing(false);
  };

  const handleClose = () => {
    reset();
    onCancel();
  };

  const updateRow = (key: number, patch: Partial<ImportPreviewRow>) => {
    setPreview((rows) =>
      rows.map((row) => (row.key === key ? { ...row, ...patch } : row))
    );
  };

  const handleFile = async (file: File) => {
    setParsing(true);
    setParseErrors([]);
    try {
      const result = await parseBookingProductImportFile(file);
      if (result.products.length === 0) {
        message.error(t("bookingWizard.productBulkImport.noProductsFound"));
        setPreview([]);
        return false;
      }
      setPreview(productsToPreviewRows(result.products));
      setParseErrors(result.errors.slice(0, 5));
      if (result.skippedRows > 0) {
        message.info(
          t("bookingWizard.productBulkImport.skippedRows", {
            count: result.skippedRows,
          })
        );
      }
    } catch (error) {
      console.error("Failed to parse product import file:", error);
      const code = error instanceof Error ? error.message : "parse_failed";
      const key =
        code === "missing_name_column"
          ? "bookingWizard.productBulkImport.missingNameColumn"
          : code === "insufficient_rows"
            ? "bookingWizard.productBulkImport.insufficientRows"
            : "bookingWizard.productBulkImport.parseFailed";
      message.error(t(key));
      setPreview([]);
    } finally {
      setParsing(false);
    }
    return false;
  };

  const handleImport = () => {
    if (!canImport) {
      message.error(
        t("bookingWizard.productBulkImport.fixErrorsBeforeImport", {
          count: invalidRowCount,
        })
      );
      return;
    }
    const products = preview.map(toImportedBookingProduct);
    onImport(products);
    message.success(
      t("bookingWizard.productBulkImport.importSuccess", {
        count: products.length,
      })
    );
    reset();
  };

  const fieldStatus = (
    index: number,
    field: BookingProductImportField
  ): "error" | undefined => (rowErrors[index]?.[field] ? "error" : undefined);

  const fieldTooltip = (index: number, field: BookingProductImportField) => {
    const key = rowErrors[index]?.[field];
    return key ? validationMessage(key) : undefined;
  };

  return (
    <Modal
      title={t("bookingWizard.productBulkImport.title")}
      open={open}
      onCancel={handleClose}
      width={1000}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={handleClose}>
          {t("common.cancel")}
        </Button>,
        <Button
          key="import"
          type="primary"
          disabled={!canImport}
          onClick={handleImport}
        >
          {t("bookingWizard.productBulkImport.importButton", {
            count: preview.length,
          })}
        </Button>,
      ]}
    >
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <Text type="secondary">
          {t("bookingWizard.productBulkImport.description")}
        </Text>

        <Space wrap>
          <Button
            icon={<DownloadOutlined />}
            onClick={downloadBookingProductImportTemplate}
          >
            {t("bookingWizard.productBulkImport.downloadTemplate")}
          </Button>
          <Upload
            accept=".xlsx,.xls,.csv"
            maxCount={1}
            fileList={fileList}
            beforeUpload={(file) => {
              setFileList([file]);
              void handleFile(file);
              return false;
            }}
            onRemove={() => {
              setFileList([]);
              setPreview([]);
              setParseErrors([]);
            }}
          >
            <Button icon={<UploadOutlined />} loading={parsing}>
              {t("bookingWizard.productBulkImport.uploadFile")}
            </Button>
          </Upload>
        </Space>

        {parseErrors.length > 0 && (
          <Alert
            type="warning"
            showIcon
            message={t("bookingWizard.productBulkImport.rowErrors")}
            description={
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {parseErrors.map((err) => (
                  <li key={err}>{err}</li>
                ))}
              </ul>
            }
          />
        )}

        {preview.length > 0 && (
          <>
            <Text strong>
              {t("bookingWizard.productBulkImport.previewTitle", {
                count: preview.length,
              })}
            </Text>

            {invalidRowCount > 0 ? (
              <Alert
                type="error"
                showIcon
                message={t("bookingWizard.productBulkImport.validationSummary", {
                  count: invalidRowCount,
                })}
                description={t(
                  "bookingWizard.productBulkImport.validationHint"
                )}
              />
            ) : (
              <Alert
                type="success"
                showIcon
                message={t("bookingWizard.productBulkImport.validationPassed")}
              />
            )}

            <Table
              size="small"
              pagination={{ pageSize: 8, hideOnSinglePage: true }}
              dataSource={preview}
              rowKey="key"
              scroll={{ x: true }}
              columns={[
                {
                  title: t("products.name"),
                  dataIndex: "name",
                  width: 160,
                  render: (_, record, index) => (
                    <Tooltip title={fieldTooltip(index, "name")}>
                      <Input
                        value={record.name}
                        status={fieldStatus(index, "name")}
                        onChange={(e) =>
                          updateRow(record.key, { name: e.target.value })
                        }
                      />
                    </Tooltip>
                  ),
                },
                {
                  title: t("products.hsCode"),
                  dataIndex: "hs_code",
                  width: 110,
                  render: (_, record, index) => (
                    <Tooltip title={fieldTooltip(index, "hs_code")}>
                      <Input
                        value={record.hs_code ?? ""}
                        status={fieldStatus(index, "hs_code")}
                        onChange={(e) =>
                          updateRow(record.key, {
                            hs_code: e.target.value || null,
                          })
                        }
                      />
                    </Tooltip>
                  ),
                },
                {
                  title: t("products.ean"),
                  dataIndex: "eanText",
                  width: 140,
                  render: (_, record, index) => (
                    <Tooltip title={fieldTooltip(index, "ean")}>
                      <Input
                        value={record.eanText}
                        maxLength={13}
                        status={fieldStatus(index, "ean")}
                        onChange={(e) => {
                          const eanText = e.target.value.replace(/\D/g, "");
                          updateRow(record.key, {
                            eanText,
                            ean: eanText ? Number(eanText) : null,
                          });
                        }}
                      />
                    </Tooltip>
                  ),
                },
                {
                  title: t("products.pieces"),
                  dataIndex: "pieces",
                  width: 90,
                  render: (_, record, index) => (
                    <Tooltip title={fieldTooltip(index, "pieces")}>
                      <InputNumber
                        min={1}
                        precision={0}
                        value={record.pieces ?? undefined}
                        status={fieldStatus(index, "pieces")}
                        style={{ width: "100%" }}
                        onChange={(v) =>
                          updateRow(record.key, { pieces: v ?? null })
                        }
                      />
                    </Tooltip>
                  ),
                },
                {
                  title: t("products.cartons"),
                  dataIndex: "cartons",
                  width: 90,
                  render: (_, record, index) => (
                    <Tooltip title={fieldTooltip(index, "cartons")}>
                      <InputNumber
                        min={1}
                        precision={0}
                        value={record.cartons ?? undefined}
                        status={fieldStatus(index, "cartons")}
                        style={{ width: "100%" }}
                        onChange={(v) =>
                          updateRow(record.key, { cartons: v ?? null })
                        }
                      />
                    </Tooltip>
                  ),
                },
                {
                  title: `${t("products.grossWeight")} (kg)`,
                  dataIndex: "gross_weight",
                  width: 110,
                  render: (_, record, index) => (
                    <Tooltip title={fieldTooltip(index, "gross_weight")}>
                      <InputNumber
                        min={0.1}
                        step={0.1}
                        value={record.gross_weight ?? undefined}
                        status={fieldStatus(index, "gross_weight")}
                        style={{ width: "100%" }}
                        onChange={(v) =>
                          updateRow(record.key, { gross_weight: v ?? null })
                        }
                      />
                    </Tooltip>
                  ),
                },
                {
                  title: t("products.cbm"),
                  dataIndex: "cbm",
                  width: 90,
                  render: (_, record, index) => (
                    <Tooltip title={fieldTooltip(index, "cbm")}>
                      <InputNumber
                        min={0.001}
                        step={0.001}
                        value={record.cbm ?? undefined}
                        status={fieldStatus(index, "cbm")}
                        style={{ width: "100%" }}
                        onChange={(v) =>
                          updateRow(record.key, { cbm: v ?? null })
                        }
                      />
                    </Tooltip>
                  ),
                },
                {
                  title: `${t("products.value")} (USD)`,
                  dataIndex: "value",
                  width: 110,
                  render: (_, record, index) => (
                    <Tooltip title={fieldTooltip(index, "value")}>
                      <InputNumber
                        min={0}
                        value={record.value ?? undefined}
                        status={fieldStatus(index, "value")}
                        style={{ width: "100%" }}
                        onChange={(v) =>
                          updateRow(record.key, { value: v ?? null })
                        }
                      />
                    </Tooltip>
                  ),
                },
              ]}
            />
          </>
        )}
      </Space>
    </Modal>
  );
};
